import { useDebounceFn } from '@vueuse/core'

import type { WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'
import type { AssessmentTemplateStatus, AssessmentType, Grade } from './enums'

import { useSidebarPrivileges } from '@entities/viewer'

import { useAssessmentTemplatesStore } from '../assessment-templates/store'
import { useAssessmentTemplatesApi } from '../../api/assessment-templates/list.hook'
import {
  assessmentTypeOptions,
  disciplineCodeOptions,
  gradeOptions,
  templateStatusOptions,
} from './assessment-template-options'

const SEARCH_DEBOUNCE_MS = 400

interface FiltersAssessmentTemplatesReturn {
  refSearch: WritableComputedRef<string>
  refType: WritableComputedRef<AssessmentType | undefined>
  refDisciplineCode: WritableComputedRef<DisciplineCode | undefined>
  refGrade: WritableComputedRef<Grade | undefined>
  refStatus: WritableComputedRef<AssessmentTemplateStatus | undefined>
  typeOptions: AppSelectOption<AssessmentType>[]
  disciplineOptions: AppSelectOption<DisciplineCode>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  statusOptions: AppSelectOption<AssessmentTemplateStatus>[]
  canManage: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  openCreateModal: () => void
}

export const useFiltersAssessmentTemplates = (): FiltersAssessmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssessmentTemplatesStore()
  const { canManageAssessmentTemplates } = useSidebarPrivileges()

  /**
   * The one place a filter change becomes a request: write the store, push the query, refetch. The
   * query is the source of truth on reload (the `assessments` middleware maps it back), so a
   * filtered list is shareable and survives a refresh. `page` is dropped so a new filter never
   * lands on a page that no longer exists.
   */
  const syncQueryAndReload = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        search: store.getFilterSearch || undefined,
        type: store.getFilterType || undefined,
        discipline_code: store.getFilterDisciplineCode || undefined,
        grade: store.getFilterGrade || undefined,
        status: store.getFilterStatus || undefined,
        page: undefined,
      },
    })
    await useAssessmentTemplatesApi()
  }

  const applySearch = useDebounceFn(() => syncQueryAndReload(), SEARCH_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applySearch()
    },
  })

  const refType = computed<AssessmentType | undefined>({
    get: () => store.getFilterType ?? undefined,
    set: (value) => {
      store.setFilterType(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refDisciplineCode = computed<DisciplineCode | undefined>({
    get: () => store.getFilterDisciplineCode ?? undefined,
    set: (value) => {
      store.setFilterDisciplineCode(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refGrade = computed<Grade | undefined>({
    get: () => store.getFilterGrade ?? undefined,
    set: (value) => {
      store.setFilterGrade(value ?? null)
      void syncQueryAndReload()
    },
  })

  const refStatus = computed<AssessmentTemplateStatus | undefined>({
    get: () => store.getFilterStatus ?? undefined,
    set: (value) => {
      store.setFilterStatus(value ?? null)
      void syncQueryAndReload()
    },
  })

  const canManage = computed<boolean>(() => canManageAssessmentTemplates.value)
  const isLoading = computed<boolean>(() => store.isLoading)

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  return {
    refSearch,
    refType,
    refDisciplineCode,
    refGrade,
    refStatus,
    typeOptions: assessmentTypeOptions,
    disciplineOptions: disciplineCodeOptions,
    gradeSelectOptions: gradeOptions,
    statusOptions: templateStatusOptions,
    canManage,
    isLoading,
    openCreateModal,
  }
}
